import { useEffect, useState } from 'react';

/**
 * Hook to manage dark mode data attribute for Tailwind styles.
 * Supports both explicit data attribute and automatic detection from the app's color mode.
 *
 * @param darkModeDataAttribute - Optional explicit data attribute name. If provided, uses it directly.
 * @returns The data attribute name to use for dark mode styling
 */
export default function useDarkModeAttribute(darkModeDataAttribute?: string) {
  const [useAppDataAttribute, setUseAppDataAttribute] = useState(!darkModeDataAttribute);

  useEffect(() => {
    if (darkModeDataAttribute) return;

    const appEl = document.querySelector('[data-color-mode]');
    setUseAppDataAttribute(['light', 'dark'].includes(appEl?.getAttribute('data-color-mode') || ''));

    /*
     * @note: This is to support dark mode in tailwind styles. In the preview
     * panel, we want to be able to set it to based on a toggle, independent of
     * the rest of the pages scheming. So we can simple use the data attribute
     * selector.
     *
     * I'm maybe not savvy enough to figure out how to combine that with a
     * media query for tailwind, so we check if the main app is using system
     * settings (when `data-color-mode` === `null` or `'system'`). Or if it's
     * been set, we switch to using the data attribute.
     */
    const observer = new MutationObserver((records: MutationRecord[]) => {
      records.forEach(record => {
        if (!(record.target instanceof HTMLElement)) return;

        const { dataset } = record.target;
        setUseAppDataAttribute(['dark', 'light'].includes(dataset.colorMode || ''));
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-color-mode'],
    });

    return () => observer.disconnect();
  }, [darkModeDataAttribute]);

  return useAppDataAttribute ? 'data-color-mode' : darkModeDataAttribute;
}
